/*
 *  -----------------------------------------------------------------------
 *    <copyright file="actions-donut-chart.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { HC_COLORS_MUTED, OpraColours } from '../../../../interfaces/opra-chart.interface';
import { useTranslation } from 'hooks';
import { CentralMonitoringActionFields } from 'queries/generated/fragments.generated';
import DonutChart, { IOpraDonutSeries } from 'views/components/core/charts/donut-chart';
import Loading from 'views/components/core/loading';
import PanelFrame from 'views/components/core/panel-frame';

interface ActionsDonutChartProps {
    actions: any[];
    datasetDate: string;
}

const ActionsDonutChart = ({ actions, datasetDate }: ActionsDonutChartProps) => {
    const [chartData, setChartData] = useState<IOpraDonutSeries[]>(null);
    const { t } = useTranslation();

    useEffect(() => {
        const colors = [
            HC_COLORS_MUTED[OpraColours.Red],
            HC_COLORS_MUTED[OpraColours.Amber],
            HC_COLORS_MUTED[OpraColours.Green],
        ];
        const array = [...actions]
            .filter(
                (item: CentralMonitoringActionFields) =>
                    !item.completedDate && moment(datasetDate) >= moment(item.requestDate),
            )
            .map((item: CentralMonitoringActionFields) => {
                let color = '';
                const today = moment();
                const requestDate = moment(item.requestDate);
                const diff = today.diff(requestDate, 'days');
                if (diff <= 14) color = colors[2];
                if (diff > 14 && diff <= 28) color = colors[1];
                if (diff > 28) color = colors[0];
                return {
                    ...item,
                    color,
                };
            });
        setChartData(
            colors.map(
                (series, idx): IOpraDonutSeries => ({
                    name: [
                        t('cm:actionsAgingLegendRed'),
                        t('cm:actionsAgingLegendYellow'),
                        t('cm:actionsAgingLegendGreen'),
                    ][idx],
                    colour: idx + 1,
                    segmentSize: array.filter((a) => series === a.color).length,
                }),
            ),
        );
    }, [actions]);

    if (!chartData) return <Loading inside />;

    const hasData = chartData.reduce((prevVal: number, curVal): number => prevVal + (curVal?.segmentSize ?? 0), 0) > 0;

    return (
        <PanelFrame title={t('cm:actionsAging')}>
            {(getRef: any) => (
                <DonutChart
                    getRef={getRef}
                    title={t('cm:actionsAging')}
                    hidePlotTitle
                    data={chartData}
                    entityType={t('cm:actions')}
                    onClick={() => {}}
                    noDataMessage={t('cm:actionsAgingNoDataMessage')}
                />
            )}
        </PanelFrame>
    );
};

export default ActionsDonutChart;
